import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateNovelDialog } from './CreateNovelDialog';
import { NovelCard } from './novel-card';
import { SubscriptionLimitGate } from '@/components/subscription/subscription-limit-gate';

import { EMPTY_NOVEL_SUMMARIES } from '@/lib/types';
import type { Novel, CollaboratedNovel } from '@/lib/types';

import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useUserNovels } from '@/context/user-novels-context';
import { useExampleNovel } from '@/hooks/use-example-novel';

export function NovelSelectionGrid() {
  const { t } = useTranslation();
  const { backend, user } = useFirebase();
  const { setSelectedNovelId } = useNovelRepository();
  const navigate = useNavigate();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const resolveResponsive = useResponsiveValue();

  const { userData } = useUserProfile();
  const { ownedNovels: novels, isLoading } = useUserNovels();
  const collaboratedNovels = userData.collaboratedNovels || [];
  const { showExampleNovel, exampleNovelEntry, dismissExampleNovel } = useExampleNovel();

  const allNovels = useMemo(() => {
    const list: Array<Novel> = [];
    if (novels) {
      novels.forEach((n) => {
        list.push(n);
      });
    }
    collaboratedNovels.forEach((collab: CollaboratedNovel) => {
      const key = `${collab.ownerId};${collab.novelId}`;
      list.push({
        id: key,
        title: `${collab.novelTitle} (${collab.role === 'collaborator' ? 'Editor' : 'Viewer'})`,
        ownerId: collab.ownerId,
        status: 'draft',
        size: 'small',
        collaborators: {},
        readers: {},
        storageLayout: {
          entities: {},
          narrative: {},
        },
        entitiesMeta: {
          summaries: {},
          settings: {},
          codexCategories: {},
          codexCategoryParents: {},
        },
        narrativeMeta: {
          summaries: {
            contents: {},
          },
        },
        canonRevision: '1',
        aiSettings: {
          intelligenceTier: 'eco',
        },
        noteFolders: {},
        summaries: EMPTY_NOVEL_SUMMARIES,
        settings: {},
        codexCategories: {},
        codexCategoryParents: {},
      });
    });
    // Append the per-language example novel last
    if (showExampleNovel && exampleNovelEntry) {
      list.push({
        id: `${exampleNovelEntry.ownerId};${exampleNovelEntry.novelId}`,
        title: exampleNovelEntry.novelTitle || '',
        ownerId: exampleNovelEntry.ownerId,
        status: 'draft',
        size: 'small',
        collaborators: {},
        readers: {},
        storageLayout: { entities: {}, narrative: {} },
        entitiesMeta: { summaries: {}, settings: {}, codexCategories: {}, codexCategoryParents: {} },
        narrativeMeta: { summaries: { contents: {} } },
        canonRevision: '1',
        aiSettings: { intelligenceTier: 'eco' },
        noteFolders: {},
        summaries: EMPTY_NOVEL_SUMMARIES,
        settings: {},
        codexCategories: {},
        codexCategoryParents: {},
        isExample: true,
      });
    }
    return list;
  }, [novels, collaboratedNovels, showExampleNovel, exampleNovelEntry]);

  const handleSelectNovel = (novelId: string) => {
    setSelectedNovelId(novelId);
    navigate({ to: '/' });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 3 }}>
          {t('dashboard.selection_grid.title')}
        </Typography>

        <Grid container spacing={3}>
          {/* Create New Novel Card - Always First */}
          <Grid id="novel-selection-grid-step" size={resolveResponsive({ xs: 12, sm: 6, md: 4 })}>
            <SubscriptionLimitGate limit="ownedNovels" currentCount={novels.length}>
              <NovelCard isCreateCard onCreate={() => setDialogOpen(true)} />
            </SubscriptionLimitGate>
          </Grid>

          {/* Existing Novels */}
          {allNovels.map((novel) => (
            <Grid key={novel.id} size={resolveResponsive({ xs: 12, sm: 6, md: 4 })}>
              <NovelCard
                novel={novel}
                onSelect={handleSelectNovel}
                isExample={novel.isExample === true}
                onDismiss={novel.isExample === true ? dismissExampleNovel : undefined}
              />
            </Grid>
          ))}
        </Grid>

        {allNovels.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t('dashboard.selection_grid.empty_state')}
            </Typography>
          </Box>
        )}
      </Box>

      <CreateNovelDialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}
